import { useCallback, useState } from "react";
import {
  Accept,
  DropzoneInputProps,
  DropzoneRootProps,
  FileRejection,
  useDropzone,
} from "react-dropzone";

import { Modality, SourceType } from "@/components/graphql/schema.graphql";
import { FilepickerFile } from "@/components/ui-v2/FilePicker/types";
import { useFileUpload } from "@/hooks/useFileUpload";
import useShowSnackbar from "@/hooks/useShowSnackbar";
import { convertVideoFileToFilepickerFile } from "@/utils/video";

interface UseDropzoneFileUploadProps {
  maxFiles?: number;
  accept?: Accept;
  sourceType?: SourceType;
  modality?: Modality;
  disabled?: boolean;
  noClick?: boolean;
  noKeyboard?: boolean;
  onUploadStart?: () => void;
  onUploadSuccess?: (files: FilepickerFile[]) => void;
}

interface UseDropzoneFileUploadReturn {
  getRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
  getInputProps: (props?: DropzoneInputProps) => DropzoneInputProps;
  isDragActive: boolean;
  isUploading: boolean;
  uploadingCount: number;
  uploadFiles: (files: File[]) => Promise<void>;
  open: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

export const useDropzoneFileUpload = (
  props: UseDropzoneFileUploadProps,
): UseDropzoneFileUploadReturn => {
  const {
    maxFiles,
    accept,
    sourceType,
    modality,
    disabled,
    noClick,
    noKeyboard,
    onUploadStart,
    onUploadSuccess,
  } = props;

  const { showError } = useShowSnackbar();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const { upload } = useFileUpload();

  const handleDropAccepted = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      // Note: This code may not be executed since errors are handled in handleDropRejected when maxFiles is set.
      if (maxFiles && files.length > maxFiles) {
        showError(`You can only upload up to ${maxFiles} file(s) at a time.`);
        return;
      }

      try {
        if (onUploadStart) {
          onUploadStart();
        }

        setIsUploading(true);
        setUploadingCount(files.length);

        const uploadPromises = files.map(async (file) => {
          const fileType = file.type.split("/")[0];
          // When selecting a video file when the modality is IMAGE, the user will need to select a frame from the video
          if (fileType === "video" && modality === "IMAGE") {
            return convertVideoFileToFilepickerFile(file);
          }

          return upload(file, {
            sourceType,
          });
        });

        const uploadedFiles = await Promise.allSettled(uploadPromises);
        const successfullyUploadedFiles = uploadedFiles
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value)
          .filter(Boolean);

        if (successfullyUploadedFiles.length === 0) {
          showError("Failed to upload any files.");
          return;
        }

        if (onUploadSuccess) {
          onUploadSuccess(successfullyUploadedFiles);
        }
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("An unknown upload error occurred");
        console.error("Error uploading files:", err);
        showError("Failed to upload files.");
      } finally {
        setIsUploading(false);
        setUploadingCount(0);
      }
    },
    [
      maxFiles,
      showError,
      upload,
      modality,
      sourceType,
      onUploadStart,
      onUploadSuccess,
    ],
  );

  const handleDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const { code, message } = fileRejections[0].errors[0];

        if (code === "too-many-files" && maxFiles)
          showError(`You can only upload up to ${maxFiles} file(s) at a time.`);
        else showError(message);
      }
    },
    [showError, maxFiles],
  );

  const { getRootProps, getInputProps, isDragActive, open, inputRef } =
    useDropzone({
      onDropAccepted: handleDropAccepted,
      onDropRejected: handleDropRejected,
      multiple: typeof maxFiles !== "undefined" ? maxFiles > 1 : true,
      accept,
      disabled,
      noClick,
      noKeyboard,
      maxFiles,
    });

  return {
    open,
    getRootProps,
    getInputProps,
    isDragActive,
    isUploading,
    uploadingCount,
    uploadFiles: handleDropAccepted,
    inputRef,
  };
};
